<template>
  <div>
    <AllegroAccountHeader
      :accountPk="route.params.pk"
      :accountData="accountData"
      activeTab="overview"
    >
      <div class="row">
        <div class="col-lg-12">
          <div class="ibox account-connection-actions">
            <button
              v-if="
                canAssociateAccount &&
                !accountData?.configuration?.device_flow &&
                !accountData?.configuration?.access_token
              "
              :disabled="
                !accountData?.configuration || accountData.loadingDeviceCode
              "
              :title="
                !accountData?.configuration
                  ? 'Konfiguracja OAuth dla konta jest wymagana przed połączeniem z Allegro'
                  : ''
              "
              class="btn btn-warning"
              @click="getDeviceFlow(accountData)"
            >
              <i
                :class="
                  accountData.loadingDeviceCode
                    ? 'fa fa-spinner fa-spin'
                    : 'fa fa-link'
                "
              ></i>
              <span>{{
                accountData.loadingDeviceCode
                  ? "Generowanie kodu..."
                  : "Połącz z Allegro"
              }}</span>
            </button>
            <a
              v-if="
                canAssociateAccount && accountData?.configuration?.device_flow
              "
              class="btn btn-success"
              :href="
                accountData.configuration.device_flow.verification_uri_complete
              "
              target="_blank"
            >
              <i class="fa fa-external-link"></i>
              <span>Zatwierdź dostęp w Allegro</span>
            </a>
            <button
              v-if="
                canRevokeAccountToken &&
                accountData?.configuration?.access_token
              "
              :disabled="accountData.loadingRevoke"
              class="btn btn-danger"
              @click="openRevokeModal(accountData)"
            >
              <i
                :class="
                  accountData.loadingRevoke
                    ? 'fa fa-spinner fa-spin'
                    : 'fa fa-unlink'
                "
              ></i>
              <span>{{
                accountData.loadingRevoke ? "Rozłączanie..." : "Rozłącz"
              }}</span>
            </button>
          </div>
        </div>
      </div>
      <div class="row">
        <div class="col-lg-12">
          <div class="ibox">
            <div class="ibox-title ibox-title-flex">
              <h5>Dane konta</h5>
              <div class="ibox-title-operations">
                <router-link
                  v-if="canUpdateAccount"
                  :to="`/allegro/accounts/${route.params.pk}/update`"
                  class="btn btn-primary"
                >
                  <i class="fa fa-edit"></i> Edytuj konto
                </router-link>
              </div>
            </div>
            <div class="ibox-content">
              <div class="row">
                <div class="col-md-6">
                  <dl class="dl-horizontal">
                    <dt v-if="accountData?.configuration?.device_flow">
                      Kod urządzenia ważny:
                    </dt>
                    <dd v-if="accountData?.configuration?.device_flow">
                      {{ accountData.configuration.device_flow.expires_at }}
                    </dd>
                    <dt v-if="accountData?.configuration?.access_token">
                      Token ważny:
                    </dt>
                    <dd v-if="accountData?.configuration?.access_token">
                      {{ accountData.configuration.access_token.expires_at }}
                    </dd>
                    <dt>Nazwa:</dt>
                    <dd>{{ accountData.name || "-" }}</dd>
                    <dt>E-mail:</dt>
                    <dd>{{ accountData.email || "-" }}</dd>
                    <dt>Login:</dt>
                    <dd>{{ accountData.login || "-" }}</dd>
                    <dt>ID Allegro:</dt>
                    <dd>{{ accountData.external_id || "-" }}</dd>
                    <dt>Status połączenia:</dt>
                    <dd>
                      <span
                        v-if="
                          !accountData?.configuration?.device_flow &&
                          !accountData?.configuration?.access_token
                        "
                        class="label label-warning"
                      >
                        <i class="fa fa-exclamation-triangle"></i>
                        Wymaga połączenia
                      </span>
                      <span
                        v-else-if="
                          accountData?.configuration?.device_flow &&
                          accountData.loadingAccessToken
                        "
                        class="label label-success"
                      >
                        <i class="fa fa-spinner fa-spin"></i>
                        W trakcie połączenia
                      </span>
                      <span
                        v-else-if="accountData?.configuration?.access_token"
                        class="label label-primary"
                      >
                        <i class="fa fa-check-circle"></i>
                        Połączone
                      </span>
                      <span v-else class="label label-success">
                        W trakcie połączenia
                      </span>
                    </dd>
                  </dl>
                </div>
                <div class="col-md-6">
                  <dl class="dl-horizontal">
                    <dt>Data utworzenia:</dt>
                    <dd>{{ accountData.date_added || "-" }}</dd>
                    <dt>Utworzono przez:</dt>
                    <dd>{{ accountData.created_by || "-" }}</dd>
                    <dt>Data modyfikacji:</dt>
                    <dd>{{ accountData.date_modified || "-" }}</dd>
                    <dt>Zmodifikowano przez:</dt>
                    <dd>{{ accountData.last_modified_by || "-" }}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AllegroAccountHeader>

    <Modal
      v-if="accountToRevoke"
      title="Rozłączenie konta"
      :content="revokeModalContent"
      @callbackOk="revokeAccessToken"
      @callbackClose="closeRevokeModal"
    />
  </div>
</template>

<script>
import { computed, ref, watch } from "vue";
import { useStore } from "vuex";
import { useRoute } from "vue-router";
import AllegroAccountHeader from "./AllegroAccountHeader.vue";
import Modal from "../caffeprint/Modal.vue";
import { hasPermission } from "../../permissions";
import { useAllegroConnection } from "../../composables/useAllegroConnection";
import { allegroAccountPermissionsConst } from "../../constants/allegroConstants";

export default {
  name: "AllegroAccountOverview",
  components: { AllegroAccountHeader, Modal },
  setup() {
    const route = useRoute();
    const store = useStore();
    const accountData = ref({});
    const accountDataRaw = computed(() => store.getters.getAllegroAccountData);

    // --- Current admin permissions ---
    const canUpdateAccount = computed(() =>
      hasPermission(allegroAccountPermissionsConst.update)
    );
    // --- End: current admin permissions ---

    const loadAccount = () => {
      store.dispatch("getAllegroAccount", route.params.pk);
    };

    // --- Connecting the account to Allegro (device flow) ---
    const {
      accountToRevoke,
      revokeModalContent,
      canAssociateAccount,
      canRevokeAccountToken,
      refreshAuthInfo,
      getDeviceFlow,
      openRevokeModal,
      closeRevokeModal,
      revokeAccessToken,
    } = useAllegroConnection({
      resolveAccount: (configurationPk) =>
        accountData.value?.configuration?.pk === configurationPk
          ? accountData.value
          : null,
      onChange: () => loadAccount(),
    });
    // --- End: connecting the account to Allegro ---

    loadAccount();
    watch(
      () => accountDataRaw.value,
      (newValue) => {
        if (newValue?.status !== 200) {
          return;
        }
        accountData.value = {
          ...newValue.data,
          loadingDeviceCode: false,
          loadingAccessToken: false,
          loadingRevoke: false,
        };

        // An account left mid-authorization keeps being polled after a
        // reload, the same way the overview does it in the old admin panel.
        refreshAuthInfo(accountData.value);
      },
      { deep: true }
    );

    return {
      route,
      accountData,
      canUpdateAccount,
      canAssociateAccount,
      canRevokeAccountToken,
      accountToRevoke,
      revokeModalContent,
      getDeviceFlow,
      openRevokeModal,
      closeRevokeModal,
      revokeAccessToken,
    };
  },
};
</script>

<style scoped>
/* Connection status badge. The spacing is set on the badge itself, because
   Font Awesome (dom.watch) swaps every <i class="fa ..."> for an <svg> that
   carries no scope attribute, so scoped rules cannot target the icon. */
.label {
  align-items: center;
  display: inline-flex;
  gap: 4px;
}

.account-connection-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.ibox-title-flex {
  align-items: center;
  display: flex;
  justify-content: space-between;
}

.ibox-title-flex h5 {
  margin: 0;
}

.ibox-title-operations {
  margin-left: auto;
}
</style>
